import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  ExternalLink,
  HelpCircle,
  Pause,
  type LucideIcon,
} from 'lucide-react';
import { useDrawer } from '@/app/providers';
import { GITHUB_ISSUES_BASE } from '@/shared/github-repo';
import { VERSIONS, type VersionEntry } from '@/versions/registry';
import {
  MEETINGS,
  type ActionRef,
  type ActionState,
  type Meeting,
  type MeetingEntry,
} from '@/versions/v1/shared/meeting-log';
import { listComments } from '@/versions/v1/comments/api';
import type { CommentResponse } from '@/versions/v1/comments/types';

// Changelog scene — meeting-driven timeline + per-release notes from the
// version registry.
//
// Each meeting is a section. Each entry under it is one discussion point,
// with an actor citation chip and one or more action chips. Click a chip →
// the universal drawer opens with the verbatim quote + action detail +
// shipped state + pointers back to the meeting source files.
//
// The "Release notes" tab lists per-version changelog blocks from
// `src/versions/registry.ts` — one section per registered MAJOR, each
// carrying inline `(#N · Name)` GitHub-issue credits where applicable.

// Extract the leading version slug from a changelog section heading like
// `v1.3 · parity close-out — …` so each per-version block becomes
// deep-linkable as `…/changelog#v1.3`.
function versionAnchor(heading: string): string | null {
  const match = heading.match(/^v[\d.]+/);
  return match ? match[0] : null;
}

// Linkify `#NN` patterns in a changelog bullet so reviewer credits like
// `(#19 · Greg)` jump straight to the GitHub issue. Splits the string
// into a mix of text and anchor nodes so React renders them inline.
function renderItemWithIssueLinks(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /#(\d+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const num = match[1];
    parts.push(
      <a
        key={`issue-${key++}-${num}`}
        href={`${GITHUB_ISSUES_BASE}${num}`}
        target="_blank"
        rel="noreferrer"
        style={{
          color: 'var(--accent-ore)',
          textDecoration: 'none',
          fontWeight: 500,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        #{num}
      </a>,
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

const STATE_ICON: Record<ActionState, LucideIcon> = {
  shipped: CheckCircle2,
  'in-flight': Clock,
  decided: HelpCircle,
  planned: Circle,
  blocked: Pause,
};

const STATE_COLOR: Record<ActionState, string> = {
  shipped: 'var(--state-online)',
  'in-flight': 'var(--accent-ore)',
  decided: 'var(--state-info)',
  planned: 'var(--ink-tertiary)',
  blocked: 'var(--state-blocked)',
};

function ActorChip({ name }: { name: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--surface-2)',
        color: 'var(--ink-secondary)',
        fontSize: 11,
        fontWeight: 500,
        border: '1px solid var(--hairline)',
      }}
    >
      <span style={{ color: 'var(--ink-tertiary)' }}>@</span>
      {name}
    </span>
  );
}

function ActionChip({ action }: { action: ActionRef }) {
  const Icon = STATE_ICON[action.state];
  const color = STATE_COLOR[action.state];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 4,
        background: 'transparent',
        color: 'var(--ink-secondary)',
        fontSize: 11,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        border: '1px solid var(--hairline)',
      }}
      title={`${action.id} · ${action.state}`}
    >
      <Icon size={11} style={{ color }} aria-hidden="true" />
      <span style={{ color: 'var(--ink-primary)' }}>{action.shortId}</span>
      {action.state === 'shipped' && action.shippedIn && (
        <span style={{ color: 'var(--ink-tertiary)' }}>
          · {action.shippedIn}
        </span>
      )}
    </span>
  );
}

function EntryRow({
  meeting,
  entry,
  onOpen,
}: {
  meeting: Meeting;
  entry: MeetingEntry;
  onOpen: (meetingId: string, entryId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(meeting.id, entry.id)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        padding: '10px 12px',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ color: 'var(--ink-primary)', fontSize: 13.5, lineHeight: 1.5 }}>
        {entry.text}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {entry.actors.map((a) => (
          <ActorChip key={a} name={a} />
        ))}
        {entry.actions.map((action) => (
          <ActionChip key={action.id} action={action} />
        ))}
      </div>
    </button>
  );
}

function MeetingSection({
  meeting,
  onOpen,
  defaultOpen,
}: {
  meeting: Meeting;
  onOpen: (meetingId: string, entryId: string) => void;
  /** First meeting in the list opens by default; the rest start collapsed.
   *  Click the header (or the chevron) to toggle. */
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const headerId = `${meeting.id}-header`;
  const bodyId = `${meeting.id}-body`;
  return (
    <section
      id={meeting.id}
      style={{
        position: 'relative',
        paddingLeft: 40,
        paddingBottom: open ? 32 : 16,
        scrollMarginTop: 64,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 2,
          top: 8,
          width: 11,
          height: 11,
          borderRadius: '50%',
          background: 'var(--accent-ore)',
          border: '2px solid var(--bg-base)',
          boxShadow: '0 0 0 1px var(--hairline-strong)',
        }}
      />
      <button
        id={headerId}
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'block',
          marginBottom: open ? 12 : 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            marginBottom: 4,
          }}
        >
          <time
            style={{
              fontSize: 11,
              color: 'var(--ink-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {meeting.date}
          </time>
          <span
            className="stage-chip tabular"
            style={{ fontSize: 9 }}
          >
            {meeting.attendees.length}{' '}
            {meeting.attendees.length === 1 ? 'attendee' : 'attendees'}
          </span>
          {!open && (
            <span
              className="tabular"
              style={{
                fontSize: 10,
                color: 'var(--ink-tertiary)',
                marginLeft: 'auto',
                paddingRight: 4,
              }}
            >
              {meeting.entries.length}{' '}
              {meeting.entries.length === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <ChevronRight
            size={14}
            aria-hidden="true"
            style={{
              color: 'var(--ink-tertiary)',
              flexShrink: 0,
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 160ms ease',
            }}
          />
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--ink-primary)',
              margin: 0,
              letterSpacing: '-0.005em',
            }}
          >
            {meeting.title}
          </h2>
        </div>
        {open && (
          <div
            style={{
              marginTop: 4,
              marginLeft: 22,
              color: 'var(--ink-tertiary)',
              fontSize: 12,
            }}
          >
            {meeting.attendees.join(' · ')}
          </div>
        )}
      </button>
      {open && (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {meeting.entries.map((e) => (
            <EntryRow key={e.id} meeting={meeting} entry={e} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}

function VersionReleaseSection({
  version,
  isFirst,
}: {
  version: VersionEntry;
  isFirst: boolean;
}) {
  if (version.changelog.length === 0) return null;
  return (
    <section
      id={version.id}
      style={{
        marginTop: isFirst ? 0 : 48,
        paddingTop: isFirst ? 0 : 32,
        borderTop: isFirst ? undefined : '1px solid var(--hairline-strong)',
        scrollMarginTop: 64,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          fontWeight: 600,
          color: 'var(--ink-tertiary)',
          marginBottom: 8,
        }}
      >
        Per-release notes
      </div>
      <h2
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: 'var(--ink-primary)',
          margin: '0 0 6px',
        }}
      >
        {version.label}
      </h2>
      <div
        className="mb-5"
        style={{ color: 'var(--ink-tertiary)', fontSize: 12, lineHeight: 1.6 }}
      >
        {version.summary}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {version.changelog.map((section) => {
          const anchor = versionAnchor(section.heading);
          return (
            <div
              key={section.heading}
              id={anchor ?? undefined}
              style={anchor ? { scrollMarginTop: 64 } : undefined}
            >
              <h3
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink-primary)',
                  margin: '0 0 8px',
                }}
              >
                {section.heading}
              </h3>
              <ul
                style={{
                  listStyle: 'disc',
                  paddingLeft: 18,
                  margin: 0,
                  color: 'var(--ink-secondary)',
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {section.items.map((item, idx) => (
                  <li key={idx}>{renderItemWithIssueLinks(item)}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReleaseNotesView() {
  const versionsWithNotes = VERSIONS.filter((v) => v.changelog.length > 0);
  if (versionsWithNotes.length === 0) {
    return (
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-tertiary)',
          padding: '24px 12px',
          textAlign: 'center',
        }}
      >
        No release notes yet — append bullets to each version&apos;s{' '}
        <code style={{ fontSize: 11 }}>changelog</code> array in{' '}
        <code style={{ fontSize: 11 }}>src/versions/registry.ts</code>.
      </div>
    );
  }
  return (
    <>
      <div
        className="mb-5"
        style={{ color: 'var(--ink-tertiary)', fontSize: 12, lineHeight: 1.6 }}
      >
        Each block is one frozen sub-version from the registry. Reviewer-driven
        closures cite the GitHub issue and reviewer name inline as{' '}
        <code style={{ fontSize: 11, color: 'var(--ink-secondary)' }}>
          (#N · Name)
        </code>{' '}
        — click the issue number to open the original comment thread. Anchors
        like <code>#v1.3</code> deep-link to a specific version block.
      </div>
      {versionsWithNotes.map((version, idx) => (
        <VersionReleaseSection key={version.id} version={version} isFirst={idx === 0} />
      ))}
    </>
  );
}

type ChangelogTab = 'meetings' | 'releases' | 'comments';

const TABS: Array<{ key: ChangelogTab; label: string; count?: number }> = [
  { key: 'meetings', label: 'Meetings' },
  { key: 'comments', label: 'Comments' },
  { key: 'releases', label: 'Release notes' },
];

// ---------------------------------------------------------------------------
// Comments tab — fetches recent reviewer comments from the worker
// (`/api/comments`, proxies GitHub Issues). Filter by state (open / closed /
// all); pagination is client-side (slice to visibleCount, "Load more" reveals
// the next batch). No deep linking to individual comments yet — clicking a
// row opens the GitHub issue in a new tab.
// ---------------------------------------------------------------------------

type CommentFilter = 'open' | 'closed' | 'all';

const COMMENT_FILTERS: Array<{ key: CommentFilter; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const PAGE_SIZE = 15;

function CommentStatusChip({ state }: { state: CommentResponse['state'] }) {
  const isOpen = state === 'open';
  return (
    <span
      className="tabular"
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 999,
        background: isOpen ? 'var(--accent-ore-soft)' : 'var(--surface-2)',
        color: isOpen ? 'var(--accent-ore)' : 'var(--ink-tertiary)',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {isOpen ? 'Open' : 'Closed'}
    </span>
  );
}

function CommentRow({ comment }: { comment: CommentResponse }) {
  const path = comment.component.split('.').join(' › ');
  const firstLine =
    comment.body
      .replace(/^\*\*From\*\*:[^\n]*\n\n?/, '')
      .split('\n')[0]
      .trim() || '(no body)';
  return (
    <a
      href={comment.htmlUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        padding: '10px 12px',
        marginBottom: 4,
        background: 'var(--surface-1)',
        border: '1px solid var(--hairline)',
        borderRadius: 6,
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-2)';
        e.currentTarget.style.borderColor = 'var(--hairline-strong)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--surface-1)';
        e.currentTarget.style.borderColor = 'var(--hairline)';
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <CommentStatusChip state={comment.state} />
        <span
          style={{
            fontSize: 11,
            color: 'var(--ink-tertiary)',
            fontWeight: 500,
            letterSpacing: '0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
          title={`#${comment.number} · ${path}`}
        >
          #{comment.number} · {path}
        </span>
        {comment.replyCount > 0 && (
          <span
            className="tabular"
            style={{
              fontSize: 10,
              color: 'var(--ink-tertiary)',
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--surface-2)',
            }}
            title={`${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
          >
            {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}
          </span>
        )}
        <ExternalLink
          size={11}
          aria-hidden
          style={{ color: 'var(--ink-tertiary)', flexShrink: 0 }}
        />
      </div>
      <div
        style={{
          fontSize: 13,
          color:
            comment.state === 'closed'
              ? 'var(--ink-tertiary)'
              : 'var(--ink-primary)',
          textDecoration: comment.state === 'closed' ? 'line-through' : 'none',
          lineHeight: 1.45,
          marginBottom: 4,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {firstLine}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 11,
          color: 'var(--ink-tertiary)',
        }}
      >
        <span style={{ fontWeight: 500, color: 'var(--ink-secondary)' }}>
          {comment.author}
        </span>
        <span>·</span>
        <span className="tabular">{timeAgo(comment.createdAt)}</span>
        <span>·</span>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={comment.route}
        >
          {comment.route}
        </span>
      </div>
    </a>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const ms = Date.now() - then;
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

function CommentsView() {
  const [filter, setFilter] = useState<CommentFilter>('open');
  const [comments, setComments] = useState<CommentResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setComments(null);
    setVisibleCount(PAGE_SIZE);
    listComments('all', filter)
      .then((items) => {
        if (cancelled) return;
        setComments(items);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const total = comments?.length ?? 0;
  const visible = comments?.slice(0, visibleCount) ?? [];
  const hasMore = total > visibleCount;

  return (
    <section style={{ paddingBottom: 64 }}>
      {/* Filter strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: 'var(--ink-tertiary)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginRight: 4,
          }}
        >
          Show
        </span>
        {COMMENT_FILTERS.map((f) => {
          const isActive = f.key === filter;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 500,
                background: isActive ? 'var(--accent-ore-soft)' : 'transparent',
                color: isActive ? 'var(--accent-ore)' : 'var(--ink-secondary)',
                border: `1px solid ${isActive ? 'var(--accent-ore)' : 'var(--hairline)'}`,
                cursor: 'pointer',
                transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
              }}
            >
              {f.label}
            </button>
          );
        })}
        {comments && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: 'var(--ink-tertiary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {total === 0
              ? 'No comments'
              : `${total} ${total === 1 ? 'comment' : 'comments'}`}
          </span>
        )}
      </div>

      {loading && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-tertiary)',
            padding: '24px 12px',
            textAlign: 'center',
          }}
        >
          Loading comments…
        </div>
      )}

      {error && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--state-blocked)',
            background: 'var(--surface-2)',
            border: '1px solid var(--hairline)',
            borderRadius: 6,
            padding: '12px 14px',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Couldn't load comments
          </div>
          <div style={{ color: 'var(--ink-tertiary)' }}>{error}</div>
          <div style={{ color: 'var(--ink-tertiary)', marginTop: 6 }}>
            The worker proxies GitHub Issues — if it's unreachable (e.g. in
            local dev without the Worker running) this is the expected fail
            mode.
          </div>
        </div>
      )}

      {!loading && !error && comments && comments.length === 0 && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-tertiary)',
            padding: '24px 12px',
            textAlign: 'center',
          }}
        >
          No {filter === 'all' ? '' : filter} comments yet.
        </div>
      )}

      {!loading && !error && visible.length > 0 && (
        <>
          <div>
            {visible.map((c) => (
              <CommentRow key={c.number} comment={c} />
            ))}
          </div>
          {hasMore && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setVisibleCount((prev) => prev + PAGE_SIZE)
                }
                style={{
                  padding: '8px 18px',
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: 'var(--ink-primary)',
                  background: 'var(--surface-1)',
                  border: '1px solid var(--hairline)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'background 120ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--surface-2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--surface-1)';
                }}
              >
                Load more · {total - visibleCount} remaining
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ChangelogTabBar({
  active,
  onPick,
}: {
  active: ChangelogTab;
  onPick: (tab: ChangelogTab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Changelog tabs"
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        marginBottom: 24,
        borderBottom: '1px solid var(--hairline)',
      }}
    >
      {TABS.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onPick(tab.key)}
            style={{
              position: 'relative',
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 500,
              color: isActive ? 'var(--ink-primary)' : 'var(--ink-secondary)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 120ms ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: -1,
              letterSpacing: '0.01em',
            }}
          >
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span
                className="tabular"
                style={{
                  fontSize: 10,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: isActive
                    ? 'var(--accent-ore-soft)'
                    : 'var(--surface-2)',
                  color: isActive ? 'var(--accent-ore)' : 'var(--ink-tertiary)',
                  fontWeight: 600,
                }}
              >
                {tab.count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: 0,
                  height: 2,
                  background: 'var(--accent-ore)',
                  borderRadius: '2px 2px 0 0',
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function ChangelogScene() {
  const { open: openDrawer } = useDrawer();
  const [params, setParams] = useSearchParams();

  // Tab state lives in the URL (?tab=meetings|releases) so deep-links stay
  // shareable and the per-release anchors (#v1.3 etc.) continue to
  // work — when a fragment-link lands the user on the page, the tab
  // pre-selects to the release block that owns that anchor.
  const tabParam = params.get('tab');
  const fragment = typeof window !== 'undefined' ? window.location.hash : '';
  const fragmentIsRelease = /^#v\d/.test(fragment);
  const activeTab: ChangelogTab =
    tabParam === 'releases'
      ? 'releases'
      : tabParam === 'meetings'
        ? 'meetings'
        : tabParam === 'comments'
          ? 'comments'
          : fragmentIsRelease
            ? 'releases'
            : 'meetings';

  const onOpen = useMemo(
    () => (meetingId: string, entryId: string) => {
      const meeting = MEETINGS.find((m) => m.id === meetingId);
      const entry = meeting?.entries.find((e) => e.id === entryId);
      openDrawer('meeting-citation', {
        title: entry?.text ?? 'Meeting citation',
        kicker: meeting?.title ?? 'MEETING',
        data: { meetingId, entryId },
      });
    },
    [openDrawer],
  );

  function setTab(tab: ChangelogTab) {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
  }

  return (
    <div
      data-comment-anchor="ChangelogScene"
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '48px 32px 96px',
        background: 'var(--bg-base)',
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <header style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontWeight: 600,
              color: 'var(--ink-tertiary)',
              marginBottom: 6,
            }}
          >
            Changelog
          </div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: 'var(--ink-primary)',
              margin: 0,
              letterSpacing: '-0.01em',
            }}
          >
            How the proof of concept changed
          </h1>
          <div
            style={{
              marginTop: 12,
              color: 'var(--ink-secondary)',
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            {activeTab === 'meetings' && (
              <>
                Each entry is one discussion point from a real meeting,
                cited to who said it. Click an entry to see the verbatim
                quote and trace it to the action that fell out of it.
              </>
            )}
            {activeTab === 'releases' && (
              <>
                Per-version release notes from{' '}
                <code>src/versions/registry.ts</code>. Anchors like{' '}
                <code>#v1.3</code> deep-link to a specific version block.
              </>
            )}
            {activeTab === 'comments' && (
              <>
                Recent reviewer comments fetched live from GitHub Issues
                (the worker proxies the API). Filter by status and load
                more as needed. Click any row to open the GitHub issue
                with full thread, replies, and labels.
              </>
            )}
          </div>
        </header>

        <ChangelogTabBar active={activeTab} onPick={setTab} />

        {activeTab === 'meetings' && (
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              position: 'relative',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 7,
                top: 12,
                bottom: 12,
                width: 1,
                background: 'var(--hairline-strong)',
              }}
            />
            {MEETINGS.map((meeting, idx) => (
              <li key={meeting.id}>
                <MeetingSection
                  meeting={meeting}
                  onOpen={onOpen}
                  defaultOpen={idx === 0}
                />
              </li>
            ))}
          </ol>
        )}
        {activeTab === 'releases' && <ReleaseNotesView />}
        {activeTab === 'comments' && <CommentsView />}
      </div>
    </div>
  );
}
